/**
 * External dependencies
 */
import Swiper from 'swiper/bundle';
import 'swiper/css';
import gsap from 'gsap';
import { loadLottieAnimation } from '../utils/load-lottie';

/**
 * アニメーション設定
 */
const ANIMATION_DURATION = 0.5;
const ARC_ANGLE = 12; // 各スライドの傾き角度
const LOTTIE_PLAY_DELAY = 800; // アクティブ化から Lottie 再生までの遅延 (ms)

/**
 * スライドの傾きを更新
 * @param {Swiper}  swiperInstance - Swiperインスタンス
 * @param {boolean} animate        - アニメーションするか
 */
function updateSlideRotations(swiperInstance, animate = true) {
	const { slides, activeIndex } = swiperInstance;

	slides.forEach((slide, index) => {
		// アクティブスライドからの相対位置を計算
		let relativePos = index - activeIndex;

		// ループモードの場合、最短距離を計算
		const totalSlides = slides.length;
		if (Math.abs(relativePos) > totalSlides / 2) {
			if (relativePos > 0) {
				relativePos -= totalSlides;
			} else {
				relativePos += totalSlides;
			}
		}

		// 傾き角度を計算（前のスライドは-12度、次のスライドは+12度）
		const rotation = relativePos * ARC_ANGLE;

		// 垂直方向のオフセット（アーチの曲線）
		const verticalOffset = Math.abs(relativePos) * 20;

		// アニメーション適用
		if (animate) {
			gsap.to(slide, {
				rotation,
				y: verticalOffset,
				duration: ANIMATION_DURATION,
				ease: 'none',
			});
		} else {
			gsap.set(slide, {
				rotation,
				y: verticalOffset,
			});
		}
	});
}

/**
 * 各スライド内の Lottie を読み込み、swiper-slide-active が付いたら 1 回再生する。
 * 各スライドの class 変化を監視するので、Swiper のイベントには依存しない。
 * @param {HTMLElement} container - カルーセルのルート要素
 */
function setupLotties(container) {
	const items = container.querySelectorAll(
		'.wp-block-best-anniversary-blocks-interview-carusel-item'
	);

	items.forEach((item, index) => {
		const target = item.querySelector(
			'.wp-block-best-anniversary-blocks-interview-carusel-item__more'
		);
		if (!target) {
			return;
		}
		const path = target.dataset.lottie;
		if (!path) {
			return;
		}
		const animation = loadLottieAnimation({
			idPrefixScope: `interview-carusel-${ index }`,
			container: target,
			renderer: 'svg',
			loop: false,
			autoplay: false,
			path,
			rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
		});

		let playTimer;
		const playIfActive = () => {
			clearTimeout(playTimer);
			if (!item.classList.contains('swiper-slide-active')) {
				return;
			}
			// 遅延後にもう一度アクティブか確認してから再生。
			playTimer = setTimeout(() => {
				if (item.classList.contains('swiper-slide-active')) {
					animation.goToAndPlay(0, true);
				}
			}, LOTTIE_PLAY_DELAY);
		};

		// class が変わるたびに active かどうか判定して再生。
		const observer = new MutationObserver(playIfActive);
		observer.observe(item, {
			attributes: true,
			attributeFilter: ['class'],
		});

		// 初期表示でアクティブなスライドも再生。
		playIfActive();
	});
}

/**
 * Swiperを初期化
 */
function initSwiper() {
	const container = document.querySelector(
		'.wp-block-best-anniversary-blocks-interview-carusel'
	);
	if (!container) {
		return;
	}

	const prevBtn = container.querySelector('.swiper-button-prev');
	const nextBtn = container.querySelector('.swiper-button-next');

	let isAnimating = false;

	const swiper = new Swiper(container, {
		centeredSlides: true,
		loop: true,
		slidesPerView: 'auto',
		spaceBetween: 40,
		speed: 1000,
		pagination: {
			el: '.swiper-pagination',
			clickable: true,
		},
		allowTouchMove: false, // カスタムアニメーションは無効
		a11y: {
			prevSlideMessage: '前のスライド',
			nextSlideMessage: '後のスライド',
		},
		breakpoints: {
			782: {
				spaceBetween: 60,
			},
		},
		on: {
			init(swiperInstance) {
				// 初期状態の傾きを設定（アニメーションなし）
				updateSlideRotations(swiperInstance, false);
				// 複製スライドを含めて Lottie を読み込む。
				setupLotties(container);
			},
			slideChangeTransitionStart(swiperInstance) {
				// スライド変更時に傾きを更新
				updateSlideRotations(swiperInstance, true);
			},
		},
	});

	/**
	 * スライド変更を実行
	 * @param {string} direction - 'next' または 'prev'
	 */
	function changeSlide(direction) {
		if (isAnimating) {
			return;
		}
		isAnimating = true;

		if (direction === 'next') {
			swiper.slideNext();
		} else {
			swiper.slidePrev();
		}

		// アニメーション完了後にフラグをリセット
		setTimeout(
			() => {
				isAnimating = false;
			},
			ANIMATION_DURATION * 2 * 1000
		);
	}

	// カスタムナビゲーション
	if (prevBtn) {
		prevBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			changeSlide('prev');
		});
	}

	if (nextBtn) {
		nextBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			changeSlide('next');
		});
	}
}

// DOM読み込み完了後に初期化
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initSwiper);
} else {
	initSwiper();
}
